import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  alpha,
  useTheme,
  Card,
  CardContent,
} from '@mui/material';

interface CompletudeEquipe {
  ine: string;
  equipe: string;
  indicadores_zerados: string[];
  completude_pct: number;
}

interface CompletudeEixo {
  eixo: string;
  nome: string;
  indicadores: string[];
  equipes: CompletudeEquipe[];
}

interface CompletudeAlerta {
  tipo: string;
  mensagem: string;
  equipe?: string;
  eixo?: string;
}

interface CompletudeData {
  eixos: CompletudeEixo[];
  alertas: CompletudeAlerta[];
}

interface CompletudeHeatmapProps {
  data: CompletudeData;
}

/** Build flat columns: each column = one indicator from one eixo (e.g. "C2 A", "C2 B") */
interface HeatmapColumn {
  eixo: string;
  eixoNome: string;
  indicador: string;
  key: string;
}

function getCellColor(completudePct: number): string {
  if (completudePct >= 75) return '#2e7d32';
  if (completudePct >= 25) return '#e65100';
  return '#c62828';
}

function getCellBg(hasData: boolean, theme: any): string {
  if (!hasData) return alpha(theme.palette.action.disabled, 0.08);
  return 'transparent';
}

/** Short eixo label for column headers: "c2-criancas" -> "C2" */
function shortEixoLabel(eixo: string): string {
  const match = eixo.match(/^c(\d)/);
  if (match) return `C${match[1]}`;
  return eixo.split('-')[0].toUpperCase();
}

const CompletudeHeatmap: React.FC<CompletudeHeatmapProps> = ({ data }) => {
  const theme = useTheme();

  // Build columns and row data
  const { columns, rows, summary } = useMemo(() => {
    const cols: HeatmapColumn[] = [];
    for (const eixo of data.eixos) {
      for (const ind of eixo.indicadores) {
        cols.push({
          eixo: eixo.eixo,
          eixoNome: eixo.nome,
          indicador: ind,
          key: `${eixo.eixo}:${ind}`,
        });
      }
    }

    // Aggregate across eixos: build a map of ine -> { equipe, per-column data }
    const equipeMap = new Map<string, {
      equipe: string;
      cells: Map<string, { hasData: boolean; completudePct: number }>;
      avgCompletude: number;
    }>();

    for (const eixo of data.eixos) {
      for (const eq of eixo.equipes) {
        if (!eq.ine) continue;
        if (!equipeMap.has(eq.ine)) {
          equipeMap.set(eq.ine, { equipe: eq.equipe, cells: new Map(), avgCompletude: 0 });
        }
        const entry = equipeMap.get(eq.ine)!;
        for (const ind of eixo.indicadores) {
          const key = `${eixo.eixo}:${ind}`;
          const isZerado = eq.indicadores_zerados.includes(ind);
          entry.cells.set(key, { hasData: !isZerado, completudePct: eq.completude_pct });
        }
      }
    }

    // Calculate avg completude per equipe
    const rowData = Array.from(equipeMap.entries()).map(([ine, val]) => {
      const totalCells = val.cells.size;
      const cellsWithData = Array.from(val.cells.values()).filter(c => c.hasData).length;
      const avg = totalCells > 0 ? Math.round((cellsWithData / totalCells) * 100) : 0;
      return { ine, equipe: val.equipe, cells: val.cells, avgCompletude: avg };
    });

    // Sort by avg completude ascending (worst first)
    rowData.sort((a, b) => a.avgCompletude - b.avgCompletude);

    // Summary stats
    const totalEquipes = rowData.length;
    const goodCompletude = rowData.filter(r => r.avgCompletude >= 75).length;
    const goodPct = totalEquipes > 0 ? Math.round((goodCompletude / totalEquipes) * 100) : 0;

    return {
      columns: cols,
      rows: rowData,
      summary: {
        totalEquipes,
        goodCompletude,
        goodPct,
        alertCount: data.alertas.length,
      },
    };
  }, [data]);

  // Group columns by eixo for header spanning
  const eixoSpans = useMemo(() => {
    const spans: { eixo: string; label: string; nome: string; count: number }[] = [];
    for (const col of columns) {
      const last = spans[spans.length - 1];
      if (last && last.eixo === col.eixo) {
        last.count++;
      } else {
        spans.push({ eixo: col.eixo, label: shortEixoLabel(col.eixo), nome: col.eixoNome, count: 1 });
      }
    }
    return spans;
  }, [columns]);

  if (rows.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Nenhuma equipe encontrada para analise de completude.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Summary stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label={`${summary.totalEquipes} equipes`}
          size="small"
          sx={{ fontWeight: 600 }}
        />
        <Chip
          label={`${summary.goodPct}% com boa completude`}
          size="small"
          sx={{
            fontWeight: 600,
            bgcolor: alpha(summary.goodPct >= 50 ? '#2e7d32' : '#e65100', 0.1),
            color: summary.goodPct >= 50 ? '#2e7d32' : '#e65100',
          }}
        />
        {summary.alertCount > 0 && (
          <Chip
            label={`${summary.alertCount} alertas`}
            size="small"
            sx={{ fontWeight: 600, bgcolor: alpha('#c62828', 0.1), color: '#c62828' }}
          />
        )}
      </Box>

      {/* Heatmap table */}
      <TableContainer sx={{ maxHeight: 480, overflowX: 'auto' }}>
        <Table size="small" stickyHeader sx={{ minWidth: columns.length * 36 + 200 }}>
          <TableHead>
            {/* Eixo span row */}
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  bgcolor: theme.palette.background.paper,
                  borderRight: `1px solid ${theme.palette.divider}`,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  minWidth: 160,
                }}
              >
                Equipe
              </TableCell>
              <TableCell
                rowSpan={2}
                sx={{
                  position: 'sticky',
                  left: 160,
                  zIndex: 3,
                  bgcolor: theme.palette.background.paper,
                  borderRight: `1px solid ${theme.palette.divider}`,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  minWidth: 40,
                  textAlign: 'center',
                }}
              >
                %
              </TableCell>
              {eixoSpans.map((span) => (
                <Tooltip key={span.eixo} title={span.nome} arrow>
                  <TableCell
                    colSpan={span.count}
                    align="center"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      bgcolor: theme.palette.background.paper,
                      borderLeft: `1px solid ${theme.palette.divider}`,
                      py: 0.5,
                    }}
                  >
                    {span.label}
                  </TableCell>
                </Tooltip>
              ))}
            </TableRow>
            {/* Indicator letter row */}
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  align="center"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    py: 0.25,
                    px: 0.25,
                    minWidth: 28,
                    maxWidth: 32,
                    bgcolor: theme.palette.background.paper,
                  }}
                >
                  {col.indicador}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const rowColor = getCellColor(row.avgCompletude);
              return (
                <TableRow key={row.ine} hover>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      bgcolor: theme.palette.background.paper,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 160,
                      py: 0.5,
                    }}
                  >
                    <Tooltip title={`${row.equipe} (INE: ${row.ine})`} arrow>
                      <span>{row.equipe}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      position: 'sticky',
                      left: 160,
                      zIndex: 1,
                      bgcolor: theme.palette.background.paper,
                      borderRight: `1px solid ${theme.palette.divider}`,
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      color: rowColor,
                      py: 0.5,
                    }}
                  >
                    {row.avgCompletude}
                  </TableCell>
                  {columns.map((col) => {
                    const cellData = row.cells.get(col.key);
                    const hasData = cellData?.hasData ?? false;
                    const bgColor = hasData
                      ? alpha('#2e7d32', 0.15)
                      : alpha('#c62828', 0.15);
                    const noData = !cellData;

                    return (
                      <Tooltip
                        key={col.key}
                        title={
                          noData
                            ? `${col.eixoNome} ${col.indicador}: Equipe nao presente`
                            : `${col.eixoNome} ${col.indicador}: ${hasData ? 'Com dados' : 'Sem dados (numerador = 0)'}`
                        }
                        arrow
                      >
                        <TableCell
                          align="center"
                          sx={{
                            px: 0.25,
                            py: 0.5,
                            minWidth: 28,
                            maxWidth: 32,
                            bgcolor: noData
                              ? getCellBg(false, theme)
                              : bgColor,
                            borderLeft: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                          }}
                        >
                          {noData ? (
                            <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>-</Typography>
                          ) : hasData ? (
                            <Box sx={{
                              width: 14,
                              height: 14,
                              borderRadius: '3px',
                              bgcolor: '#2e7d32',
                              mx: 'auto',
                            }} />
                          ) : (
                            <Box sx={{
                              width: 14,
                              height: 14,
                              borderRadius: '3px',
                              bgcolor: '#c62828',
                              mx: 'auto',
                            }} />
                          )}
                        </TableCell>
                      </Tooltip>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5, alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Legenda:</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#2e7d32' }} />
          <Typography variant="caption" color="text.secondary">Com dados</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: '#c62828' }} />
          <Typography variant="caption" color="text.secondary">Sem dados</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: alpha(theme.palette.action.disabled, 0.2) }} />
          <Typography variant="caption" color="text.secondary">Equipe nao presente</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CompletudeHeatmap;
